<template>
  <layout-content-vertical-nav :nav-menu-items="navMenuItems">
    <slot></slot>
    <!-- Slot: Navbar -->
    <template #navbar="{ isVerticalNavMenuActive, toggleVerticalNavMenuActive }">
      <div
        class="navbar-content-container"
        :class="{ 'expanded-search': shallShowFullSearch }"
      >
        <!-- Left Content: Search -->
        <div
          class="d-flex align-center"
          data-onboarding="top-search"
        >
          <v-icon
            v-if="$vuetify.breakpoint.mdAndDown"
            class="me-3"
            @click="toggleVerticalNavMenuActive"
          >
            {{ icons.mdiMenu }}
          </v-icon>
          <app-bar-search
            :shall-show-full-search.sync="shallShowFullSearch"
            :data="appBarSearchData"
            :filter="searchFilterFunc"
            :search-query.sync="appBarSearchQuery"
            @update:shallShowFullSearch="
              handleShallShowFullSearchUpdate(isVerticalNavMenuActive, toggleVerticalNavMenuActive)
            "
          ></app-bar-search>
        </div>

        <!-- Right Content: I18n, Light/Dark, Notification & User Dropdown -->
        <div
          class="d-flex align-center right-row"
          data-onboarding="user-menu"
        >
          <v-btn
            v-if="getWarningSubscription == 'yes' && $vuetify.breakpoint.mdAndDown"
            color="warning"
            fab
            small
            class="me-4"
            @click="showPricingPlan"
          >
            <v-icon color="tertiary">{{ icons.mdiAlert }}</v-icon>
          </v-btn>
          <v-chip
            v-if="getWarningSubscription == 'yes' && !$vuetify.breakpoint.mdAndDown"
            color="warning"
            class="me-4"
            @click="showPricingPlan"
          >
            <v-icon
              left
              color="tertiary"
            >
              {{ icons.mdiAlert }}
            </v-icon>
            <span class="tertiary--text"> Pagamento pendente</span>
          </v-chip>
          <v-btn
            v-if="getUser && !getUser.email_verified_at && $vuetify.breakpoint.mdAndDown"
            color="error"
            fab
            small
            class="me-4"
            style="cursor: pointer"
            @click="showVerifyDialog = true"
          >
            <v-icon color="white">{{ icons.mdiEmailAlertOutline }}</v-icon>
          </v-btn>
          <v-chip
            v-if="getUser && !getUser.email_verified_at && !$vuetify.breakpoint.mdAndDown"
            color="error"
            class="me-4"
            style="cursor: pointer"
            @click="showVerifyDialog = true"
          >
            <v-icon
              left
              color="white"
            >
              {{ icons.mdiEmailAlertOutline }}
            </v-icon>
            <span class="white--text"> Verifique seu e-mail</span>
          </v-chip>
          <v-btn v-if="$route.name === 'dashboard'" icon small class="mx-2" @click="restartTour">
            <v-icon>{{ icons.mdiCompassOutline }}</v-icon>
          </v-btn>
          <AppBarStyle v-if="!$vuetify.breakpoint.mdAndDown" />
          <app-bar-theme-switcher class="mx-4"></app-bar-theme-switcher>
          <app-bar-notification></app-bar-notification>
          <app-bar-user-menu></app-bar-user-menu>
        </div>
      </div>
    </template>

    <!-- Dialog: Verificação de Email -->
    <v-dialog
      v-model="showVerifyDialog"
      max-width="450"
      persistent
    >
      <v-card>
        <v-card-title class="text-h6">
          <v-icon left color="primary">{{ icons.mdiEmailCheckOutline }}</v-icon>
          Verificar E-mail
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="editEmail"
            outlined
            label="E-mail"
            placeholder="seu@email.com"
            hide-details="auto"
            class="mb-4"
            :error-messages="emailError"
          ></v-text-field>
          <v-btn
            text
            small
            color="primary"
            :loading="savingEmail"
            :disabled="savingEmail || editEmail === getUser.email"
            @click="handleUpdateEmail"
            class="mb-4"
          >
            Atualizar e-mail
          </v-btn>
          <v-divider class="mb-4"></v-divider>
          <p class="mb-2">
            Enviamos um código de verificação para <strong>{{ editEmail || getUser.email }}</strong>
          </p>
          <v-text-field
            v-model="verifyCode"
            outlined
            label="Código de verificação"
            placeholder="000000"
            maxlength="6"
            hide-details="auto"
            class="mb-4 text-center"
            :error-messages="verifyError"
            @keypress="onCodeInput"
          ></v-text-field>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-btn
            text
            color="secondary"
            :loading="resending"
            :disabled="resending"
            @click="handleResendCode"
          >
            Reenviar código
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            text
            color="secondary"
            :disabled="verifying"
            @click="showVerifyDialog = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            :loading="verifying"
            :disabled="verifying || verifyCode.length !== 6"
            @click="handleVerifyCode"
          >
            Confirmar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Slot: Footer -->
    <template #footer>
      <div class="d-flex justify-space-between">
        <span
          >COPYRIGHT &copy; {{ new Date().getFullYear() }}
          <a
            href="https://themeselection.com"
            class="text-decoration-none"
            >ThemeSelection</a
          ><span class="d-none d-md-inline">, All rights Reserved</span></span
        >
        <div class="align-center d-none d-md-flex">
          <span>Hand-crafted &amp; Made with</span>
          <v-icon
            color="error"
            class="ms-2"
          >
            {{ icons.mdiHeartOutline }}
          </v-icon>
        </div>
      </div>
    </template>

    <template #v-app-content>
      <app-customizer class="d-none d-md-block"></app-customizer>
    </template>
  </layout-content-vertical-nav>
</template>

<script>
import LayoutContentVerticalNav from '@/@core/layouts/variants/content/vertical-nav/LayoutContentVerticalNav.vue'
import navMenuItems from '@/navigation/vertical'
import AppCustomizer from '@core/layouts/components/app-customizer/AppCustomizer.vue'

// App Bar Components
import AppBarSearch from '@core/layouts/components/app-bar/AppBarSearch.vue'
import AppBarUserMenu from '@/components/AppBarUserMenu.vue'

import { mdiMenu, mdiHeartOutline, mdiAlert, mdiCompassOutline, mdiEmailAlertOutline, mdiEmailCheckOutline } from '@mdi/js'

import { getVuetify } from '@core/utils'

// Search Data
import appBarSearchData from '@/assets/app-bar-search-data'
import AppBarNotification from '@core/layouts/components/app-bar/AppBarNotification.vue'
import AppBarThemeSwitcher from '@core/layouts/components/app-bar/AppBarThemeSwitcher.vue'
import AppBarStyle from '@core/layouts/components/app-bar/AppBarStyle.vue'
import { mapGetters, mapMutations } from 'vuex'
import axios from '@axios'

export default {
  components: {
    LayoutContentVerticalNav,
    AppBarNotification,
    AppBarThemeSwitcher,
    AppBarSearch,
    AppBarUserMenu,
    AppBarStyle,
    AppCustomizer,
  },
  data() {
    return {
      appBarSearchQuery: '',
      shallShowFullSearch: false,
      maxItemsInGroup: 5,
      totalItemsInGroup: {
        pages: 0,
        files: 0,
        contacts: 0,
      },
      appBarSearchData,
      showVerifyDialog: false,
      editEmail: '',
      verifyCode: '',
      verifying: false,
      resending: false,
      savingEmail: false,
      verifyError: '',
      emailError: '',
      icons: {
        mdiMenu,
        mdiHeartOutline,
        mdiAlert,
        mdiCompassOutline,
        mdiEmailAlertOutline,
        mdiEmailCheckOutline,
      },
    }
  },
  watch: {
    appBarSearchQuery() {
      this.totalItemsInGroup = {
        pages: 0,
        files: 0,
        contacts: 0,
      }
    },
    showVerifyDialog(val) {
      if (val) {
        this.editEmail = this.getUser.email || ''
        this.verifyCode = ''
        this.verifyError = ''
        this.emailError = ''
      }
    },
  },
  computed: {
    ...mapGetters('Login', ['getWarningSubscription', 'getUser']),
    navMenuItems() {
      return navMenuItems
    },
  },
  mounted() {
    this.$root.$on('open-verify-dialog', () => {
      this.showVerifyDialog = true
    })
  },
  beforeDestroy() {
    this.$root.$off('open-verify-dialog')
  },
  methods: {
    ...mapMutations('Login', ['setUser']),
    searchFilterFunc(item, queryText, itemText) {
      if (item.header || item.divider) return true

      const normalizeText = value =>
        (value || '')
          .toString()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLocaleLowerCase()

      const itemGroup = (() => {
        if (item.to !== undefined) return 'pages'
        if (item.size !== undefined) return 'files'
        if (item.email !== undefined) return 'contacts'

        return null
      })()

      const normalizedItemText = normalizeText(itemText)
      const normalizedQueryText = normalizeText(queryText)
      const isMatched = normalizedItemText.indexOf(normalizedQueryText) > -1

      if (isMatched) {
        if (itemGroup === 'pages') this.totalItemsInGroup.pages += 1
        else if (itemGroup === 'files') this.totalItemsInGroup.files += 1
        else if (itemGroup === 'contacts') this.totalItemsInGroup.contacts += 1
      }

      return this.appBarSearchQuery && isMatched && this.totalItemsInGroup[itemGroup] <= this.maxItemsInGroup
    },
    handleShallShowFullSearchUpdate(isVerticalNavMenuActive, toggleVerticalNavMenuActive) {
      const $vuetify = getVuetify()
      if ($vuetify.breakpoint.mdAndDown && isVerticalNavMenuActive) {
        toggleVerticalNavMenuActive()
      }
    },
    showPricingPlan() {
      this.$router.push('/assinatura')
    },
    restartTour() {
      if (window.restartTour) window.restartTour()
    },
    onCodeInput(e) {
      const char = String.fromCharCode(e.keyCode)
      if (!/[0-9]/.test(char)) {
        e.preventDefault()
      }
    },
    handleVerifyCode() {
      if (this.verifyCode.length !== 6) return

      this.verifying = true
      this.verifyError = ''

      axios
        .post('/auth/verify-code', {
          email: this.getUser.email,
          code: this.verifyCode,
        })
        .then(response => {
          this.setUser(response.data.user)
          this.showVerifyDialog = false
          this.verifyCode = ''
          this.$toast.success('E-mail verificado com sucesso!')
        })
        .catch(error => {
          const msg = error.response?.data?.error || 'Código inválido. Tente novamente.'
          this.verifyError = msg
        })
        .finally(() => {
          this.verifying = false
        })
    },
    handleResendCode() {
      this.resending = true

      axios
        .post('/auth/resend-code', {
          email: this.editEmail,
        })
        .then(() => {
          this.$toast.success('Código reenviado para seu e-mail.')
        })
        .catch(error => {
          const msg = error.response?.data?.error || 'Erro ao reenviar código.'
          this.$toast.error(msg)
        })
        .finally(() => {
          this.resending = false
        })
    },
    handleUpdateEmail() {
      if (!this.editEmail || this.editEmail === this.getUser.email) return

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(this.editEmail)) {
        this.emailError = 'Informe um e-mail válido'
        return
      }

      this.savingEmail = true
      this.emailError = ''

      axios
        .put('/account/update', { email: this.editEmail })
        .then(response => {
          this.setUser(response.data.user)
          this.$toast.success('E-mail atualizado com sucesso!')
        })
        .catch(error => {
          const msg = error.response?.data?.errors?.email?.[0]
            || error.response?.data?.error
            || 'Erro ao atualizar e-mail.'
          this.emailError = msg
        })
        .finally(() => {
          this.savingEmail = false
        })
    },
  },
}
</script>

<style lang="scss" scoped>
.navbar-content-container {
  height: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-grow: 1;
  position: relative;
}

// ? Handle bg of autocomplete for blured appBar
.v-app-bar.bg-blur {
  .expanded-search {
    ::v-deep .app-bar-autocomplete-box div[role='combobox'] {
      background-color: transparent;
    }

    > .d-flex > button.v-icon {
      display: none;
    }

    // ===

    & > .right-row {
      visibility: hidden;
      opacity: 0;
    }

    ::v-deep .app-bar-search-toggler {
      visibility: hidden;
    }
  }
}
</style>
<style>
.card-actions {
  height: 40px;
}

.btn-actions {
  float: right;
}

.bar_top {
  height: 5px;
  background: #9a62fd;
}

.title_body {
  width: 100%;
  height: 45px;
  box-sizing: border-box;
  margin-bottom: 50px;
  border-bottom: 1px solid #eeeeee;
}

.title_body_no_margin {
  width: 100%;
  height: 45px;
  box-sizing: border-box;
  border-bottom: 1px solid #eeeeee;
}

.text_title {
  height: 40px;
  font-size: 16px;
  box-sizing: border-box;
  padding: 4px;
  float: left;
  text-align: center;
}

.btn_close {
  float: right;
}
</style>
